'use client';

import { playSfx } from '@/hooks/use-sound';
import { cn } from '@/lib/utils';

export type SegmentedOption<T extends string> = {
  value: T;
  label: string;
};

/**
 * The one segmented control for both studies: rounded-lg frame + rounded-md
 * active pad, the site's tabs geometry with the inverted active state. Kept as
 * aria-pressed buttons (not tabs role): these pick a value and swap content in
 * place, they don't own persistent panels. Plays its own toggle sound.
 */
export function SegmentedControl<T extends string>({
  label,
  labelHidden = false,
  options,
  value,
  onChange,
}: {
  label: string;
  labelHidden?: boolean;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {!labelHidden && (
        <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {label}
        </span>
      )}
      <fieldset
        aria-label={label}
        className="flex w-fit rounded-lg border border-edge p-0.5"
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={value === option.value}
            data-sfx-managed=""
            onClick={() => {
              if (value !== option.value) {
                playSfx('toggle');
                onChange(option.value);
              }
            }}
            className={cn(
              'rounded-md px-2.5 py-1 font-mono text-[11px] whitespace-nowrap transition-colors',
              value === option.value
                ? 'bg-foreground text-background'
                : 'text-muted-foreground hover:bg-accent2 hover:text-foreground'
            )}
          >
            {option.label}
          </button>
        ))}
      </fieldset>
    </div>
  );
}
